/**
 * @file service-request-launcher.tsx
 * @module app/modules/service-request/components/service-request-launcher
 * @description Header icon that opens the service request modal. It shows only to users whose role is
 * selected in Settings (General > Service Request > Allowed Role(s)) and carries a pending-request badge.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { useCallback, useEffect, useState } from 'react';
import { ReactComponent as ServiceBellIcon } from '../../../../_admin/assets/media/dashboard/service-bell.svg';
import { useAuth } from '../../auth';
import { fetchConfigurationSettingByKey } from '../../../../utils/helper.method';
import { CONFIGURATION_SETTING_KEYS } from '../../../../utils/configurationSettingKeys';
import { useLiveRefresh } from '../hooks/use-live-refresh';
import ServiceRequestModal from './service-request-modal';
import { getServiceRequestCounts } from '../services/service-request.service';
import { getCurrentUserRoleId, parseRoleIds } from '../utils/role-access';
import { getHistorySeenAt, isHistorySeenKey } from '../utils/history-seen';
import '../styles/service-request.css';

const MAX_BADGE_COUNT = 9;

/** Badge text: the count up to 9, then "9+". */
export const formatBadgeCount = (count: number) =>
  count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(count);

type ServiceRequestLauncherProps = {
  /** Button classes so the icon matches its neighbours in the header. */
  className?: string;
};

const ServiceRequestLauncher = ({
  className = '',
}: ServiceRequestLauncherProps) => {
  const { currentUser } = useAuth();
  const roleId = getCurrentUserRoleId(currentUser);
  const userId = String(currentUser?._id || '');
  const [allowed, setAllowed] = useState(false);
  const [open, setOpen] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);

  // Hidden until the setting confirms the user's role; any failure keeps it hidden.
  useEffect(() => {
    let cancelled = false;
    if (!roleId) {
      setAllowed(false);
      return;
    }
    fetchConfigurationSettingByKey(
      CONFIGURATION_SETTING_KEYS.KEY__SERVICE_REQUEST_ACCESS_ROLE_IDS
    )
      .then((setting) => {
        if (!cancelled) {
          setAllowed(
            parseRoleIds(setting?.value ?? setting?.defaultValue).includes(
              roleId
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [roleId]);

  /**
   * Badge: requests waiting for my approval, plus my requests decided since I last opened
   * History. Both are counted by the server; only the "last opened" time lives in this browser.
   */
  const loadBadgeCount = useCallback(async () => {
    try {
      const counts = await getServiceRequestCounts(
        userId ? getHistorySeenAt(userId) : undefined
      );
      setBadgeCount(counts.approvalsPending + counts.newDecisions);
    } catch {
      setBadgeCount(0);
    }
  }, [userId]);

  useEffect(() => {
    if (allowed) void loadBadgeCount();
  }, [allowed, loadBadgeCount]);

  // Live: socket event, tab focus, or a timer when there is no socket (local dev has none).
  useLiveRefresh(() => void loadBadgeCount(), allowed);

  // History opened in another tab changes the last-seen time; reload so the count matches.
  useEffect(() => {
    if (!allowed) return;
    const handleStorage = (event: StorageEvent) => {
      if (isHistorySeenKey(event.key)) void loadBadgeCount();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [allowed, loadBadgeCount]);

  if (!allowed) return null;

  return (
    <>
      <button
        type="button"
        className={`${className} sr-launcher`}
        title="Self Service"
        aria-label={
          badgeCount
            ? `Self Service, ${badgeCount} need your attention`
            : 'Self Service'
        }
        onClick={() => setOpen(true)}
      >
        {/* Inline SVG drawn in currentColor, so it follows the header's light and dark colours */}
        <ServiceBellIcon
          width={18}
          height={18}
          aria-hidden="true"
          focusable="false"
        />
        {badgeCount > 0 ? (
          <span className="sr-launcher__badge" aria-hidden="true">
            {formatBadgeCount(badgeCount)}
          </span>
        ) : null}
      </button>
      <ServiceRequestModal
        show={open}
        onHide={() => {
          setOpen(false);
          void loadBadgeCount();
        }}
      />
    </>
  );
};

export default ServiceRequestLauncher;
